<template>
  <section class="about-us">
    <div class="about-us-container">
      <div class="section-header">
        <span class="section-label">{{ t('index.aboutUs.label') }}</span>
        <h2 class="section-title">{{ t('index.aboutUs.title') }}</h2>
        <p class="section-description">{{ t('index.aboutUs.description') }}</p>
      </div>

      <div class="features-grid">
        <div v-for="(feature, index) in features"
             :key="index"
             class="feature-card">
          <img
            :src="feature.icon"
            :alt="feature.title"
            class="feature-icon"
            width="48"
            height="48"
            loading="lazy"
            decoding="async"
          >
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-description" v-html="feature.description"></p>
        </div>
      </div>

      <div class="cta-wrapper">
        <UIButton variant="primary-arrow"
                  :href="`/${currentLocale}#courses-section`"
                  :text="t('index.aboutUs.chooseCourseButton')"
                  @click="onChooseCourseClick">
        </UIButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useLocaleSwitch } from '@/composables/locale'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { currentLocale } = useLocaleSwitch()

function scrollToCoursesSection () {
  nextTick(() => {
    const el = document.getElementById('courses-section')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

function onChooseCourseClick (e: MouseEvent) {
  e.preventDefault()
  const homePath = `/${currentLocale.value}`
  if (route.path === homePath) {
    scrollToCoursesSection()
    router.replace({ path: homePath, hash: '#courses-section' })
  } else {
    router.push({ path: homePath, hash: '#courses-section' })
  }
}

const features = computed(() => [
  {
    title: t('index.aboutUs.feature1Title'),
    description: t('index.aboutUs.feature1Description'),
    icon: '/assets/images/icon/about-icon1.svg'
  },
  {
    title: t('index.aboutUs.feature2Title'),
    description: t('index.aboutUs.feature2Description'),
    icon: '/assets/images/icon/about-icon2.svg'
  },
  {
    title: t('index.aboutUs.feature3Title'),
    description: t('index.aboutUs.feature3Description'),
    icon: '/assets/images/icon/about-icon3.svg'
  }
])
</script>

<style scoped>
.about-us {
  padding: 92px 0 98px 0;
  max-width: var(--max-width-container);
  margin: 0 auto;
}

.about-us-container {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Перевизначення загальних стилів секції (базові в index.css) */
.section-header {
  margin-bottom: 40px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  width: 100%;
  margin-bottom: 18px;
}

.feature-card {
  background: #fff;
  border-radius: 16px;
  padding: 35px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s, box-shadow 0.3s;
  position: relative;
}

.feature-card:not(:nth-child(3n))::after {
  content: '';
  position: absolute;
  right: -16px;
  top: 20%;
  bottom: 20%;
  width: 1px;
  background: #E5E7EB;
}

/* .feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
} */

.feature-icon {
  width: 90px;
  height: 90px;
  margin-bottom: 24px;
  object-fit: contain;
}

.feature-title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 20px;
  line-height: 1.3;
  color: var(--color-dark-blue);
  margin: 0 0 16px 0;
}

.feature-description {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  line-height: 1.6;
  color: var(--color-grey);
  margin: 0;
}

.cta-wrapper {
  display: flex;
  justify-content: center;
}

@media (max-width: 1400px) {
  .about-us-container {
    padding: 0 60px;
  }
}

@media (max-width: 991px) {
  .about-us {
    padding: 60px 0;
  }

  .about-us-container {
    padding: 0 30px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .feature-card {
    padding: 32px 24px;
  }

  .feature-card::after {
    display: none;
  }
}

@media (max-width: 767px) {
  .about-us {
    padding: 40px 0 59px;
  }

  .about-us-container {
    padding: 0 20px;
  }
}

@media (max-width: 575px) {
  .feature-card {
    padding: 24px 20px;
  }

  .feature-title {
    font-size: var(--font-size-title);
  }

  .feature-description {
    font-size: var(--font-size-body);
  }
}
@media (max-width: 480px) {
  .section-header {
    padding: 12px 0 0 0;
  }
}
</style>
